import React, { useState } from 'react';
import NavigationHeader from '../components/NavigationHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const WEBHOOK_URL = 'https://auto.ertheo.biz/webhook/997033b4-4712-448e-b8b8-4ccce2e2f248';
const CAMP_NAME = 'Oakham-Football-Camps';

// Language-specific phone numbers
const phoneNumbers = {
  en: '(+44) 203 769 94 43',
  es: '(+34) 93 018 6626',
  fr: '(+33) 170 849 040',
  it: '(+39) 02 87 368 972'
};

const ContactPage = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const phoneNumber = phoneNumbers[language] || phoneNumbers.en;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    program: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const payload = {
        ...formData,
        // Hidden fields
        language: language,
        camp_name: CAMP_NAME,
        form_type: 'contact',
        submitted_at: new Date().toISOString()
      };

      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          program: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-6">{t('contact.heroTitle')}</h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            {t('contact.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <Card className="lg:col-span-2 p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-6">{t('contact.sendMessage')}</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="font-bold">{t('contact.fullName')} *</Label>
                    <Input 
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-bold">{t('contact.emailAddress')} *</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="font-bold">{t('contact.phoneNumber')}</Label>
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+44 123 456 7890"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="program" className="font-bold">{t('contact.interestedIn')} *</Label>
                    <select
                      id="program"
                      name="program"
                      value={formData.program}
                      onChange={handleChange}
                      required
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">{t('contact.selectProgram')}</option>
                      <option value="clinic">{t('contact.highPerformanceClinic')}</option>
                      <option value="aston-villa">{t('contact.astonVillaProgram')}</option>
                      <option value="both">{t('contact.bothPrograms')}</option>
                      <option value="general">{t('contact.generalInquiry')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="font-bold">{t('contact.message')} *</Label>
                  <Textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.messagePlaceholder')}
                    required
                    rows={6}
                    className="mt-2"
                  />
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md mb-4" data-testid="contact-success-message">
                    {t('contact.successMessage') || 'Thank you for your message! We will get back to you soon.'}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-4" data-testid="contact-error-message">
                    {t('contact.errorMessage') || 'There was an error sending your message. Please try again.'}
                  </div>
                )}

                <Button 
                  type="submit"
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white font-bold w-full md:w-auto px-10"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t('contact.sending') || 'Sending...'}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      {t('contact.sendMessageBtn')}
                    </>
                  )}
                </Button>
              </form>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="p-6 bg-gradient-to-br from-green-600 to-green-700 text-white">
                <Mail className="h-10 w-10 mb-4" />
                <h3 className="text-xl font-black mb-2">{t('contact.emailUs')}</h3>
                <p className="text-green-100 text-sm mb-2">info@oakhamfootballcamps.com</p>
                <p className="text-green-100 text-xs">{t('contact.emailResponse')}</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
                <Phone className="h-10 w-10 mb-4" />
                <h3 className="text-xl font-black mb-2">{t('contact.callUs')}</h3>
                <p className="text-blue-100 text-sm mb-2">{phoneNumber}</p>
                <p className="text-blue-100 text-xs">{t('contact.callHours')}</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-purple-600 to-purple-700 text-white">
                <MapPin className="h-10 w-10 mb-4" />
                <h3 className="text-xl font-black mb-2">{t('contact.visitUs')}</h3>
                <p className="text-purple-100 text-sm mb-2">Oakham School</p>
                <p className="text-purple-100 text-sm">Oakham, Rutland</p>
                <p className="text-purple-100 text-sm">England, LE15 6DT</p>
              </Card>

              <Card className="p-6 bg-gray-50">
                <h3 className="text-lg font-black text-gray-900 mb-3">{t('contact.quickLinks')}</h3>
                <div className="space-y-2 text-sm">
                  <a href="/prices" className="block text-green-600 hover:text-green-700 font-semibold">→ {t('contact.viewPricesDates')}</a>
                  <a href="/clinic" className="block text-green-600 hover:text-green-700 font-semibold">→ {t('contact.clinicProgramDetails')}</a>
                  <a href="/about-oakham" className="block text-green-600 hover:text-green-700 font-semibold">→ {t('contact.aboutOakhamSchool')}</a>
                  <a href="/signup" className="block text-green-600 hover:text-green-700 font-semibold">→ {t('contact.signUpNow')}</a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
